/**
 * Data Sync Management Page
 * MariaDB (RDS) と Cognito のユーザーデータ同期管理画面
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Preview as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  dataSyncApi,
  type SyncPreviewResult,
  type SyncExecutionResult,
  type SyncOptions,
  type SyncUserResult,
} from '../api/dataSyncApi';

export const DataSyncManagementPage: React.FC = () => {
  // State
  const [preview, setPreview] = useState<SyncPreviewResult | null>(null);
  const [executionResult, setExecutionResult] = useState<SyncExecutionResult | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [singleUserSyncing, setSingleUserSyncing] = useState<string | null>(null);
  const [singleUserResults, setSingleUserResults] = useState<Record<string, SyncUserResult>>({});

  // Sync options
  const [options, setOptions] = useState<SyncOptions>({
    createMissingCognitoUsers: true,
    syncAttributes: true,
    dryRun: false,
  });

  // Handlers
  const handlePreview = async () => {
    setIsLoadingPreview(true);
    setError(null);
    setExecutionResult(null);
    try {
      const result = await dataSyncApi.getPreview();
      setPreview(result);
    } catch (err) {
      setError((err as Error).message || 'プレビューの取得に失敗しました');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    setConfirmDialogOpen(false);
    setIsExecuting(true);
    setError(null);
    try {
      const result = await dataSyncApi.executeSync(options);
      setExecutionResult(result);
      // Refresh preview after execution
      if (!options.dryRun) {
        await handlePreview();
      }
    } catch (err) {
      setError((err as Error).message || '同期の実行に失敗しました');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleSyncSingleUser = async (userId: string) => {
    setSingleUserSyncing(userId);
    try {
      const result = await dataSyncApi.syncSingleUser(userId);
      setSingleUserResults(prev => ({ ...prev, [userId]: result }));
      // Refresh preview
      await handlePreview();
    } catch (err) {
      setSingleUserResults(prev => ({
        ...prev,
        [userId]: {
          userId,
          email: '',
          username: '',
          action: 'failed',
          success: false,
          error: (err as Error).message,
        },
      }));
    } finally {
      setSingleUserSyncing(null);
    }
  };

  const getTotalActions = () => {
    if (!preview) return 0;
    let count = 0;
    if (options.createMissingCognitoUsers) count += preview.summary.onlyInRdsCount;
    if (options.syncAttributes) count += preview.summary.mismatchCount;
    return count;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        データ同期管理
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        MariaDB (RDS) と Cognito のユーザーデータの整合性を確認・同期します
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Preview Button */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={isLoadingPreview ? <CircularProgress size={20} /> : <PreviewIcon />}
              onClick={handlePreview}
              disabled={isLoadingPreview || isExecuting}
            >
              {isLoadingPreview ? '確認中...' : '不整合を確認'}
            </Button>
            {preview && (
              <Typography variant="body2" color="text.secondary">
                最終確認: {new Date(preview.checkTime).toLocaleString('ja-JP')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Preview Results */}
      {preview && (
        <>
          {/* Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                サマリー
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`RDS ユーザー: ${preview.summary.totalRdsUsers}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Cognito ユーザー: ${preview.summary.totalCognitoUsers}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<WarningIcon />}
                  label={`RDSのみ: ${preview.summary.onlyInRdsCount}`}
                  color={preview.summary.onlyInRdsCount > 0 ? 'warning' : 'default'}
                />
                <Chip
                  icon={<WarningIcon />}
                  label={`Cognitoのみ: ${preview.summary.onlyInCognitoCount}`}
                  color={preview.summary.onlyInCognitoCount > 0 ? 'warning' : 'default'}
                />
                <Chip
                  icon={<WarningIcon />}
                  label={`属性不一致: ${preview.summary.mismatchCount}`}
                  color={preview.summary.mismatchCount > 0 ? 'warning' : 'default'}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Users Only in RDS */}
          {preview.usersOnlyInRds.length > 0 && (
            <Accordion defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  RDSのみに存在するユーザー ({preview.usersOnlyInRds.length}件)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ユーザー名</TableCell>
                        <TableCell>メール</TableCell>
                        <TableCell>名前</TableCell>
                        <TableCell>権限レベル</TableCell>
                        <TableCell align="center">アクション</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.usersOnlyInRds.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>
                            <Chip label={user.permissionLevel} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            {singleUserResults[user.id] ? (
                              singleUserResults[user.id].success ? (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="同期済み"
                                  color="success"
                                  size="small"
                                />
                              ) : (
                                <Tooltip title={singleUserResults[user.id].error || 'エラー'}>
                                  <Chip
                                    icon={<ErrorIcon />}
                                    label="失敗"
                                    color="error"
                                    size="small"
                                  />
                                </Tooltip>
                              )
                            ) : (
                              <Tooltip title="このユーザーをCognitoに作成">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSyncSingleUser(user.id)}
                                  disabled={singleUserSyncing === user.id || isExecuting}
                                >
                                  {singleUserSyncing === user.id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <PersonAddIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Users with Attribute Mismatch */}
          {preview.usersWithMismatch.length > 0 && (
            <Accordion defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  属性が不一致のユーザー ({preview.usersWithMismatch.length}件)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ユーザー名</TableCell>
                        <TableCell>メール</TableCell>
                        <TableCell>属性</TableCell>
                        <TableCell>RDS値</TableCell>
                        <TableCell>Cognito値</TableCell>
                        <TableCell align="center">アクション</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.usersWithMismatch.map((user) => (
                        <React.Fragment key={user.userId}>
                          {user.mismatches.map((mismatch, idx) => (
                            <TableRow key={`${user.userId}-${mismatch.attribute}`}>
                              {idx === 0 && (
                                <>
                                  <TableCell rowSpan={user.mismatches.length}>
                                    {user.username}
                                  </TableCell>
                                  <TableCell rowSpan={user.mismatches.length}>
                                    {user.email}
                                  </TableCell>
                                </>
                              )}
                              <TableCell>{mismatch.attribute}</TableCell>
                              <TableCell>
                                <Chip
                                  label={mismatch.rdsValue || '(未設定)'}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={mismatch.cognitoValue || '(未設定)'}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              </TableCell>
                              {idx === 0 && (
                                <TableCell rowSpan={user.mismatches.length} align="center">
                                  {singleUserResults[user.userId] ? (
                                    singleUserResults[user.userId].success ? (
                                      <Chip
                                        icon={<CheckCircleIcon />}
                                        label="同期済み"
                                        color="success"
                                        size="small"
                                      />
                                    ) : (
                                      <Tooltip title={singleUserResults[user.userId].error || 'エラー'}>
                                        <Chip
                                          icon={<ErrorIcon />}
                                          label="失敗"
                                          color="error"
                                          size="small"
                                        />
                                      </Tooltip>
                                    )
                                  ) : (
                                    <Tooltip title="このユーザーの属性を同期">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleSyncSingleUser(user.userId)}
                                        disabled={singleUserSyncing === user.userId || isExecuting}
                                      >
                                        {singleUserSyncing === user.userId ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <SyncIcon />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Users Only in Cognito */}
          {preview.usersOnlyInCognito.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Cognitoのみに存在するユーザー ({preview.usersOnlyInCognito.length}件)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  これらのユーザーはCognitoに存在しますがRDSには存在しません。
                  手動での確認が必要です。
                </Alert>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ユーザー名</TableCell>
                        <TableCell>メール</TableCell>
                        <TableCell>名前</TableCell>
                        <TableCell>権限レベル</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.usersOnlyInCognito.map((user) => (
                        <TableRow key={user.username}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.name || '-'}</TableCell>
                          <TableCell>
                            {user.permissionLevel ? (
                              <Chip label={user.permissionLevel} size="small" />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Sync Options and Execute */}
          {(preview.summary.onlyInRdsCount > 0 || preview.summary.mismatchCount > 0) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  一括同期オプション
                </Typography>
                <FormGroup sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.createMissingCognitoUsers}
                        onChange={(e) =>
                          setOptions({ ...options, createMissingCognitoUsers: e.target.checked })
                        }
                        disabled={preview.summary.onlyInRdsCount === 0}
                      />
                    }
                    label={`RDSのみのユーザーをCognitoに作成 (${preview.summary.onlyInRdsCount}件)`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.syncAttributes}
                        onChange={(e) =>
                          setOptions({ ...options, syncAttributes: e.target.checked })
                        }
                        disabled={preview.summary.mismatchCount === 0}
                      />
                    }
                    label={`属性の不一致を同期 (${preview.summary.mismatchCount}件)`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.dryRun}
                        onChange={(e) => setOptions({ ...options, dryRun: e.target.checked })}
                      />
                    }
                    label="ドライラン（実際には変更しない）"
                  />
                </FormGroup>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={isExecuting ? <CircularProgress size={20} /> : <SyncIcon />}
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={isExecuting || getTotalActions() === 0}
                >
                  {isExecuting ? '同期中...' : `一括同期を実行 (${getTotalActions()}件)`}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* No Issues */}
          {preview.summary.onlyInRdsCount === 0 &&
            preview.summary.mismatchCount === 0 &&
            preview.summary.onlyInCognitoCount === 0 && (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                データの不整合はありません。RDSとCognitoのユーザーデータは同期されています。
              </Alert>
            )}
        </>
      )}

      {/* Execution Result */}
      {executionResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              実行結果 {executionResult.dryRun && '(ドライラン)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`作成成功: ${executionResult.usersCreated.successCount}`}
                color="success"
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`更新成功: ${executionResult.attributesUpdated.successCount}`}
                color="success"
              />
              {(executionResult.usersCreated.failedCount > 0 ||
                executionResult.attributesUpdated.failedCount > 0) && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`失敗: ${executionResult.usersCreated.failedCount + executionResult.attributesUpdated.failedCount}`}
                  color="error"
                />
              )}
            </Box>
            {executionResult.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">エラー詳細:</Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {executionResult.errors.map((err, idx) => (
                    <li key={idx}>
                      {err.email}: {err.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>同期の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {options.dryRun ? (
              'ドライランを実行します。実際のデータは変更されません。'
            ) : (
              <>
                以下の操作を実行します：
                <ul>
                  {options.createMissingCognitoUsers && preview && preview.summary.onlyInRdsCount > 0 && (
                    <li>{preview.summary.onlyInRdsCount}件のユーザーをCognitoに作成</li>
                  )}
                  {options.syncAttributes && preview && preview.summary.mismatchCount > 0 && (
                    <li>{preview.summary.mismatchCount}件のユーザーの属性を更新</li>
                  )}
                </ul>
                この操作は取り消せません。続行しますか？
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleExecute} variant="contained" color="primary">
            {options.dryRun ? 'ドライラン実行' : '同期を実行'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSyncManagementPage;
